import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFreelancerTypes, saveFreelancerProfile, getFreelancerProfile } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const EMOJIS = {
  video_editor: '🎬',
  thumbnail_designer: '🎨',
  graphic_designer: '🖌️',
  web_developer: '💻',
  content_writer: '✍️',
  seo_expert: '🔍',
  social_media_manager: '📱',
  photographer: '📷',
  animator: '🎭',
  motion_graphics: '✨',
  ui_ux_designer: '🎯',
  app_developer: '📲',
  copywriter: '📝',
  voice_over_artist: '🎙️',
  other: '🔧'
};

const FreelancerOnboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // App State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1);
  
  // API Data
  const [freelancerTypes, setFreelancerTypes] = useState([]);
  const [experienceLevels, setExperienceLevels] = useState([]);
  const [pricingTypes, setPricingTypes] = useState([]);
  const [targetClientTypes, setTargetClientTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  
  // Form Data
  const [selectedType, setSelectedType] = useState('');
  const [customType, setCustomType] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [customSkillInput, setCustomSkillInput] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('beginner');
  const [pricingType, setPricingType] = useState('per_project');
  const [rateMin, setRateMin] = useState('');
  const [rateMax, setRateMax] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [selectedTargetClients, setSelectedTargetClients] = useState([]);
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [bio, setBio] = useState('');
  const [portfolioUrl, setPortfolioUrl] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [typesData, profileData] = await Promise.all([
          getFreelancerTypes(),
          getFreelancerProfile().catch(() => null)
        ]);
        
        if (typesData) {
          setFreelancerTypes(typesData.types || []);
          setExperienceLevels(typesData.experience_levels || []);
          setPricingTypes(typesData.pricing_types || []);
          setTargetClientTypes(typesData.target_client_types || []);
          setCurrencies(typesData.currencies || []);
        }
        
        if (profileData && profileData.is_onboarded && profileData.profile) {
          const p = profileData.profile;
          setSelectedType(p.freelancer_type || '');
          setCustomType(p.custom_type || '');
          setSelectedSkills(p.skills || []);
          setExperienceLevel(p.experience_level || 'beginner');
          setPricingType(p.pricing_type || 'per_project');
          setRateMin(p.rate_min || '');
          setRateMax(p.rate_max || '');
          setCurrency(p.currency || 'USD');
          setSelectedTargetClients(p.target_clients || []);
          setCity(p.city || '');
          setCountry(p.country || '');
          setBio(p.bio || '');
          setPortfolioUrl(p.portfolio_url || '');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load configuration. Please try refreshing.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleNext = () => setStep(prev => Math.min(prev + 1, 4));
  const handleBack = () => setStep(prev => Math.max(prev - 1, 1));
  
  const handleSkip = async () => {
    setSkipping(true);
    setError('');
    try {
      await api.post('/api/freelancer/skip-onboarding');
      localStorage.removeItem('leadgen_profile_cache');
      setSuccess('Skipped! Redirecting to search...');
      setTimeout(() => {
        window.location.href = '/search';
      }, 800);
    } catch (err) {
      console.error('Skip onboarding error:', err);
      setError('Failed to skip. Please try again.');
      setSkipping(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      await saveFreelancerProfile({
        freelancer_type: selectedType,
        custom_type: selectedType === 'other' ? customType : null,
        skills: selectedSkills,
        experience_level: experienceLevel,
        pricing_type: pricingType,
        rate_min: parseInt(rateMin) || 0,
        rate_max: parseInt(rateMax) || 0,
        currency,
        target_clients: selectedTargetClients,
        city,
        country,
        bio,
        portfolio_url: portfolioUrl
      });
      setSuccess('Profile saved successfully! Redirecting...');
      setTimeout(() => {
        window.location.href = '/search';
      }, 1000);
    } catch (err) {
      setError(err.message || 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCustomSkillKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = customSkillInput.trim();
      if (val && !selectedSkills.includes(val)) {
        setSelectedSkills([...selectedSkills, val]);
      }
      setCustomSkillInput('');
    }
  };
  
  const toggleSkill = (skill) => {
    setSelectedSkills(prev => prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]);
  };
  
  const toggleTargetClient = (client) => {
    setSelectedTargetClients(prev => prev.includes(client.id) ? prev.filter(c => c !== client.id) : [...prev, client.id]);
  };

  const suggestedSkills = freelancerTypes.find(t => t.id === selectedType)?.skills || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-900/10 via-dark-950 to-dark-950 py-12 px-4 sm:px-6">
      
      <div className="max-w-2xl mx-auto mb-6">
        <div className="flex items-center justify-between p-4 bg-zinc-900/60 backdrop-blur border border-white/5 rounded-xl">
          <div className="flex items-center gap-3">
            <span className="text-2xl">👋</span>
            <div>
              <p className="text-sm text-gray-300 font-medium">Not ready to set up your profile?</p>
              <p className="text-xs text-gray-500">You can always complete it later from settings.</p>
            </div>
          </div>
          <button
            onClick={handleSkip}
            disabled={skipping}
            className="px-5 py-2 text-sm font-medium text-gray-300 border border-white/10 rounded-lg hover:bg-zinc-800 hover:text-white hover:border-white/20 transition-all duration-200 disabled:opacity-50 flex items-center gap-2"
          >
            {skipping ? (
              <><div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Skipping...</>
            ) : (
              <>Skip for now <span className="text-lg">→</span></>
            )}
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto mb-8">
        <div className="flex items-center justify-between relative">
          {/* Progress Bar background track */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-zinc-800 -z-10 rounded-full"></div>
          {/* Progress indicator */}
          <div className={`absolute left-0 top-1/2 -translate-y-1/2 h-1 transition-all duration-500 bg-blue-500 -z-10 rounded-full`} style={{ width: `${((step - 1) / 3) * 100}%` }}></div>
          
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
              step > s ? 'bg-blue-500 text-white shadow-[0_0_10px_rgba(59,130,246,0.5)]' : 
              step === s ? 'bg-purple-500 text-white shadow-[0_0_15px_rgba(168,85,247,0.6)] ring-4 ring-purple-500/30' : 
              'bg-zinc-800 text-gray-400'
            }`}>
              {s}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-xs font-medium text-gray-400">
          <span className={step >= 1 ? 'text-blue-400' : ''}>Type</span>
          <span className={step >= 2 ? 'text-blue-400' : ''}>Skills</span>
          <span className={step >= 3 ? 'text-blue-400' : ''}>Pricing</span>
          <span className={step >= 4 ? 'text-blue-400' : ''}>Details</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto bg-zinc-900/50 backdrop-blur border border-white/10/50 rounded-2xl p-6 sm:p-8 shadow-2xl transition-all duration-500 transform relative overflow-hidden">
        
        {success && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 text-green-400 rounded-xl text-center font-medium animate-pulse">
            {success}
          </div>
        )}
        
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl font-medium">
            {error}
          </div>
        )}

        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-6 animate-fadeIn transition-opacity duration-300">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">What type of freelancer are you?</h2>
              <p className="text-gray-400">Select your primary role so AI can match you efficiently.</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {freelancerTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => {
                    setSelectedType(type.id);
                    if (!pricingType || pricingType === 'per_project') {
                      setPricingType(type.default_pricing);
                    }
                  }}
                  className={`p-4 rounded-xl border text-left transition-all duration-200 ${
                    selectedType === type.id 
                      ? 'border-blue-500 bg-blue-500/10 shadow-[0_0_15px_rgba(59,130,246,0.15)] ring-1 ring-blue-500/50' 
                      : 'border-white/10/50 bg-zinc-800/50 hover:bg-zinc-800 hover:border-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{EMOJIS[type.id] || '💼'}</span>
                    <span className={`font-semibold text-sm ${selectedType === type.id ? 'text-blue-400' : 'text-gray-200'}`}>
                      {type.name}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {selectedType === 'other' && (
              <div className="mt-4 animate-fadeIn">
                <label className="block text-sm font-medium text-gray-300 mb-2">Specify your type</label>
                <input
                  type="text"
                  value={customType}
                  onChange={e => setCustomType(e.target.value)}
                  placeholder="e.g. Database Architect"
                  className="w-full bg-zinc-800 border-white/10 border text-white rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>
            )}

            <div className="mt-8 flex justify-between">
              <button
                onClick={handleSkip}
                disabled={skipping}
                className="px-6 py-3 text-sm text-gray-400 hover:text-white transition-colors disabled:opacity-50"
              >
                {skipping ? 'Skipping...' : 'Skip for now →'}
              </button>
              <button
                onClick={handleNext}
                disabled={!selectedType || (selectedType === 'other' && !customType.trim())}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-8 animate-fadeIn transition-opacity duration-300">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Your Skills & Experience</h2>
              <p className="text-gray-400">Tell us what you're good at and your level of mastery.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Suggested Skills</label>
              <div className="flex flex-wrap gap-2 mb-4">
                {suggestedSkills.map(skill => (
                  <button
                    key={skill}
                    onClick={() => toggleSkill(skill)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      selectedSkills.includes(skill)
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-zinc-800 text-gray-300 border border-white/10 hover:border-gray-500'
                    }`}
                  >
                    {skill}
                  </button>
                ))}
              </div>
              
              <div className="relative">
                <input
                  type="text"
                  value={customSkillInput}
                  onChange={e => setCustomSkillInput(e.target.value)}
                  onKeyDown={handleCustomSkillKeyPress}
                  placeholder="Type a custom skill and press Enter to add..."
                  className="w-full bg-zinc-800 border border-white/10 text-white rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
              
              {selectedSkills.filter(s => !suggestedSkills.includes(s)).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {selectedSkills.filter(s => !suggestedSkills.includes(s)).map(skill => (
                    <span key={skill} className="px-3 py-1.5 bg-green-500/20 border border-green-500/30 text-green-400 rounded-full text-sm font-medium flex items-center gap-2">
                      {skill}
                      <button onClick={() => toggleSkill(skill)} className="hover:text-green-300 focus:outline-none">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-4">Experience Level</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {experienceLevels.map(exp => (
                  <button
                    key={exp.id}
                    onClick={() => setExperienceLevel(exp.id)}
                    className={`p-4 rounded-xl border text-center transition-all duration-200 ${
                      experienceLevel === exp.id
                        ? 'border-purple-500 bg-purple-500/10 shadow-[0_0_15px_rgba(168,85,247,0.15)] ring-1 ring-purple-500/50'
                        : 'border-white/10/50 bg-zinc-800/50 hover:bg-zinc-800 hover:border-white/10'
                    }`}
                  >
                    <div className={`font-bold ${experienceLevel === exp.id ? 'text-purple-400' : 'text-white'}`}>
                      {exp.name.split(' (')[0]}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">({exp.name.split(' (')[1] || ''}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4 flex justify-between">
              <button onClick={handleBack} className="px-6 py-3 border border-white/10 text-gray-300 font-medium rounded-xl hover:bg-zinc-800 transition-colors">
                Back
              </button>
              <button onClick={handleNext} disabled={selectedSkills.length === 0} className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] disabled:opacity-50 disabled:cursor-not-allowed">
                Next
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="space-y-8 animate-fadeIn transition-opacity duration-300">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Pricing & Target Clients</h2>
              <p className="text-gray-400">Set your average rates and the clients you're looking for.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Pricing Structure</label>
                <select
                  value={pricingType}
                  onChange={e => setPricingType(e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 text-white rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none cursor-pointer"
                >
                  {pricingTypes.map(pt => (
                    <option key={pt.id} value={pt.id}>{pt.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Currency</label>
                <select
                  value={currency}
                  onChange={e => setCurrency(e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 text-white rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none cursor-pointer"
                >
                  {currencies.map(c => (
                    <option key={c.id} value={c.id}>{c.id} ({c.symbol})</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Your Rate Range</label>
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-400">{currencies.find(c => c.id === currency)?.symbol || '$'}</span>
                  </div>
                  <input
                    type="number"
                    value={rateMin}
                    onChange={e => setRateMin(e.target.value)}
                    placeholder="Min"
                    className="w-full bg-zinc-800 border border-white/10 text-white rounded-lg pl-8 pr-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <span className="text-gray-500 font-medium">to</span>
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-400">{currencies.find(c => c.id === currency)?.symbol || '$'}</span>
                  </div>
                  <input
                    type="number"
                    value={rateMax}
                    onChange={e => setRateMax(e.target.value)}
                    placeholder="Max"
                    className="w-full bg-zinc-800 border border-white/10 text-white rounded-lg pl-8 pr-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-sm">{pricingTypes.find(p => p.id === pricingType)?.symbol}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Who are your ideal clients?</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {targetClientTypes.map(client => (
                  <button
                    key={client.id}
                    onClick={() => toggleTargetClient(client)}
                    className={`py-2.5 px-3 rounded-lg text-sm text-left transition-all border ${
                      selectedTargetClients.includes(client.id)
                        ? 'border-green-500 bg-green-500/10 text-green-400 font-medium shadow-[0_0_10px_rgba(34,197,94,0.15)] ring-1 ring-green-500/50'
                        : 'border-white/10 bg-zinc-800/50 text-gray-300 hover:border-gray-500 hover:bg-zinc-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      {client.name}
                      {selectedTargetClients.includes(client.id) && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4 flex justify-between">
              <button onClick={handleBack} className="px-6 py-3 border border-white/10 text-gray-300 font-medium rounded-xl hover:bg-zinc-800 transition-colors">
                Back
              </button>
              <button 
                onClick={handleNext} 
                disabled={selectedTargetClients.length === 0} 
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <div className="space-y-6 animate-fadeIn transition-opacity duration-300">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Almost Done — Your Details</h2>
              <p className="text-gray-400">Complete your profile to unlock custom AI lead filtering.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">City</label>
                <input type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. London" className="w-full bg-zinc-800 border border-white/10 text-white rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Country</label>
                <input type="text" value={country} onChange={e => setCountry(e.target.value)} placeholder="e.g. United Kingdom" className="w-full bg-zinc-800 border border-white/10 text-white rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" />
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">Brief Bio</label>
                <span className={`text-xs font-medium ${bio.length > 500 ? 'text-red-400' : 'text-gray-500'}`}>{bio.length}/500</span>
              </div>
              <textarea
                value={bio}
                onChange={e => setBio(e.target.value)}
                placeholder="E.g. Professional UI Designer with 5 years of experience building SaaS dashboards..."
                rows="3"
                maxLength={500}
                className="w-full bg-zinc-800 border border-white/10 text-white rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none transition-colors"
              ></textarea>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Portfolio URL (Optional)</label>
              <input type="url" value={portfolioUrl} onChange={e => setPortfolioUrl(e.target.value)} placeholder="https://your-portfolio.com" className="w-full bg-zinc-800 border border-white/10 text-white rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" />
            </div>

            <div className="mt-2 p-4 bg-zinc-800/80 rounded-xl border border-white/10">
              <h4 className="text-sm font-semibold text-white mb-2">Profile Summary</h4>
              <ul className="space-y-1.5 text-sm text-gray-300">
                <li className="flex justify-between"><span>Primary Role:</span> <span className="font-medium text-white">{freelancerTypes.find(t => t.id === selectedType)?.name || selectedType}</span></li>
                <li className="flex justify-between"><span>Total Skills:</span> <span className="font-medium text-white">{selectedSkills.length} tagged</span></li>
                <li className="flex justify-between"><span>Experience Layer:</span> <span className="font-medium text-white capitalize">{experienceLevels.find(e => e.id === experienceLevel)?.name.split(' (')[0] || experienceLevel}</span></li>
                <li className="flex justify-between"><span>Average Rate:</span> <span className="font-medium text-green-400">{currencies.find(c => c.id === currency)?.symbol}{rateMin} - {rateMax} {pricingTypes.find(p => p.id === pricingType)?.symbol}</span></li>
              </ul>
            </div>

            <div className="pt-4 flex justify-between gap-4">
              <button onClick={handleBack} disabled={saving} className="px-6 py-3 border border-white/10 text-gray-300 font-medium rounded-xl hover:bg-zinc-800 transition-colors disabled:opacity-50">
                Back
              </button>
              <button 
                onClick={handleSave} 
                disabled={saving} 
                className="flex-1 px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-xl transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Saving Matrix...</>
                ) : (
                  'Complete & Save Profile'
                )}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FreelancerOnboarding;
